import {
  Button,
  Card,
  HStack,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  backlogStatusOptions,
  getPrettyBacklogStatus,
  modelUtils,
} from '@pelicargo/utils';
import { BacklogStatus } from '@prisma/client';
import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { HiCheck, HiOutlinePlusCircle } from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useBacklogs } from '../../hooks/backlog/useBacklogs';
import { Quote } from '../../utils/types';
import { LabeledText } from '../LabeledText';
import { SimpleForm } from '../SimpleForm';
import { SimpleNativeDatePicker } from '../SimpleNativeDatePicker';
import { SimplePopoverInput } from '../SimplePopoverInput';
import { SimpleSelect } from '../SimpleSelect';

const IndividualBacklog = ({
  backlog,
}: {
  backlog: TrpcOutputs['isQuoteBacklogged']['backlogs'][0];
}) => {
  const toast = useToast();
  const { mutateAsync: upsertBacklog, isLoading: isResolving } = trpc.upsertBacklog.useMutation();
  const methods = useForm<Extract<Parameters<typeof upsertBacklog>[0], {}>>({ defaultValues: backlog });
  const { isOpen, onToggle } = useDisclosure();

  const onSubmit = async () => {
    try {
      await upsertBacklog(methods.getValues());
      toast({ title: 'Backlog updated', status: 'success' });
      onToggle();
    } catch (error) {
      toast({
        title: 'Failed to update backlog',
        description: error?.message,
        status: 'error',
      });
    }
  };

  const resolveBacklog = async () => {
    try {
      await upsertBacklog({ ...backlog, expires_at: new Date() });
      toast({ title: 'Backlog resolved', status: 'success' });
    } catch (error) {
      toast({
        title: 'Failed to resolve backlog',
        description: error?.message,
        status: 'error',
      });
    }
  };

  return (
    <Card p="2" bg="none" minW="300px">
      <Stack gap="4">
        <HStack justifyContent="space-between">
          <Text fontWeight="bold" fontSize="md">
            {getPrettyBacklogStatus(backlog.status)}
          </Text>
          <Button onClick={onToggle} colorScheme={isOpen ? 'primary' : undefined} size="xs">
            {isOpen ? 'Done' : 'Modify'}
          </Button>
        </HStack>
        <Stack>
          <HStack justifyContent="space-between" fontSize="md">
            <Text>Active Dates</Text>
            <Text as="span">
              {backlog?.starts_at ? formatInTimeZone(backlog?.starts_at, 'Etc/UTC', 'MM/dd/yyyy') : '-'} to{' '}
              {backlog?.expires_at ? formatInTimeZone(backlog?.expires_at, 'Etc/UTC', 'MM/dd/yyyy') : 'forever'}
            </Text>
          </HStack>
          <HStack justifyContent="space-between" fontSize="md">
            <Text>Next Flight</Text>
            <Text>
              {backlog?.next_flight_at
                ? formatInTimeZone(backlog?.next_flight_at, 'Etc/UTC', 'MM/dd/yyyy')
                : 'Not Specified'}
            </Text>
          </HStack>
        </Stack>
        <Show if={isOpen}>
          <SimpleForm {...methods} defaultValues={backlog} onValid={onSubmit}>
            <Stack as="form">
              <SimpleSelect name="status" label="Status" options={backlogStatusOptions} />
              <SimpleNativeDatePicker name="expires_at" label="Expires At" />
              <Show if={backlog?.status !== BacklogStatus.AtRisk}>
                <SimpleNativeDatePicker name="next_flight_at" label="Next Flight At" />
              </Show>
              <HStack justifyContent="space-between">
                <Text fontSize="md">ID# {backlog.id}</Text>
                <Button size="xs" leftIcon={<HiCheck />} onClick={resolveBacklog} isLoading={isResolving}>
                  Resolve Backlog
                </Button>
              </HStack>
            </Stack>
          </SimpleForm>
        </Show>
      </Stack>
    </Card>
  );
};

export const BacklogEditor = ({ quote }: { quote: Quote }) => {
  const toast = useToast();
  const {
    mutateAsync: upsertBacklog,
    isLoading: isAdding,
    variables: addVariables,
  } = trpc.upsertBacklog.useMutation();
  const airports = useMemo(() => modelUtils.quote.relevantBacklogAirports(quote), [quote]);
  const { backlogs } = useBacklogs(quote.id, quote.provider_id, airports);

  const display = useMemo(() => {
    const maybeBacklog = backlogs?.find((b) => b.status === BacklogStatus.Backlogged);
    const maybeAtRisk = backlogs?.find((b) => b.status === BacklogStatus.AtRisk);
    const prioritizedBacklog = maybeBacklog || maybeAtRisk;

    const untilDateFormatted = prioritizedBacklog?.expires_at
      ? formatInTimeZone(prioritizedBacklog?.expires_at, 'Etc/UTC', 'MM/dd/yyyy')
      : '-';

    if (!backlogs?.length) return 'None';
    if (backlogs.length > 1) {
      if (prioritizedBacklog) {
        const prettyAirports = backlogs.map((b) => b.airport_id).join(', ');
        return `${prettyAirports} Multiple Backlogs`;
      }
    }

    const backloggedAirports = backlogs.map((x) => x.airport_id);
    const response = `${backloggedAirports.join(', ')} ` + (maybeBacklog ? 'backlogged' : 'at risk');
    if (maybeBacklog?.expires_at) {
      return response + ` until ${untilDateFormatted}`;
    }
    return response;
  }, [backlogs]);

  const addBacklog = useCallback(
    async (airport_id: string, status: BacklogStatus) => {
      if (!quote.provider_id) {
        toast({
          title: 'Cannot create backlog',
          description: 'This quote has no airline provider.',
          status: 'error',
        });
        return;
      }

      try {
        await upsertBacklog({
          provider_id: quote.provider_id,
          airport_id,
          expires_at: addDays(new Date(), 10),
          next_flight_at: null,
          status,
          starts_at: new Date(),
        });
        toast({ title: 'Backlog created', status: 'success' });
      } catch (error) {
        toast({
          title: 'Failed to create backlog',
          description: error?.message,
          status: 'error',
        });
      }
    },
    [quote.provider_id, toast, upsertBacklog],
  );

  return (
    <SimplePopoverInput
      displayValue={<LabeledText label="Backlog" value={display} />}
      minW="150px"
      popoverProps={{ closeOnBlur: false }}
    >
      <LabeledText label="Backlog" value={display} />
      <LabeledText label="Customer Sees" value={modelUtils.backlogs.getBacklogStatus(backlogs) || '-'} />


      <Stack gap="4" w="full">
        <Show if={!airports.length}>
          <Text fontSize="sm" color="gray.600">
            No route airports on this quote — add flight segments or set origin/destination.
          </Text>
        </Show>
        {airports.map((airport) => {
          const airportBacklogs = backlogs?.filter((backlog) => backlog.airport_id === airport);
          const isAddingAirport = isAdding && addVariables?.airport_id === airport;
          return (
            <Stack key={airport} w="full">
              <Text>{airport}</Text>
              {airportBacklogs?.map((backlog) => (
                <IndividualBacklog key={backlog.id} backlog={backlog} />
              ))}
              <Show if={!airportBacklogs?.length}>
                <HStack spacing={1}>
                  <Button
                    size="xs"
                    leftIcon={<HiOutlinePlusCircle />}
                    isLoading={isAddingAirport}
                    onClick={() => addBacklog(airport, BacklogStatus.AtRisk)}
                  >
                    At Risk
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<HiOutlinePlusCircle />}
                    isLoading={isAddingAirport}
                    onClick={() => addBacklog(airport, BacklogStatus.Backlogged)}
                  >
                    Backlogged
                  </Button>
                </HStack>
              </Show>
            </Stack>
          );
        })}
      </Stack>
    </SimplePopoverInput>
  );
};
